import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import WarningIcon from '@mui/icons-material/WarningAmber'
import type { ConflictRow } from '../../data/db.js'
import { resolveConflict } from '../../data/sync/conflicts.js'

/**
 * Arbitrage d'un conflit (W-34).
 *
 * ⚠️ Ne sont présentés QUE les champs réellement en collision. Les autres ont
 * déjà été fusionnés par le serveur, et les afficher noierait la seule décision
 * que l'utilisateur a réellement à prendre. C'est aussi ce qui rend l'écran
 * supportable : en pratique, un conflit porte sur un ou deux champs.
 */

/** Libellés métier — le journal parle en colonnes SQL, pas l'utilisateur. */
const LABELS: Record<string, string> = {
  phone: 'Téléphone',
  mobile: 'Mobile',
  email: 'Email',
  first_name: 'Prénom',
  last_name: 'Nom',
  company_name: 'Raison sociale',
  address_line1: 'Adresse',
  postal_code: 'Code postal',
  city: 'Ville',
  notes: 'Notes',
  code: 'Référence',
  tax_id: 'N° fiscal',
  payment_terms_days: 'Conditions de paiement',
  name: 'Nom',
  sku: 'Référence',
  barcode: 'Code-barres',
  sale_price: 'Prix de vente',
  purchase_price: "Prix d'achat",
}

/** `payment_terms_days` (journal) ↔ `paymentTermsDays` (saisie). */
function toCamel(field: string): string {
  return field.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
}

function label(field: string): string {
  return LABELS[field] ?? toCamel(field)
}

function display(value: unknown): string {
  if (value === null || value === undefined || value === '') return '— vide —'
  if (Array.isArray(value)) return value.join(', ') || '— vide —'
  return String(value)
}

export function ConflictDialog({
  storeId,
  conflict,
  onClose,
}: {
  storeId: string
  conflict: ConflictRow | null
  onClose: () => void
}): JSX.Element | null {
  const [choice, setChoice] = useState<'local' | 'server'>('server')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!conflict) return null

  const fields = conflict.fields
  const nom =
    (conflict.serverState['companyName'] as string) ||
    [conflict.serverState['lastName'], conflict.serverState['firstName']]
      .filter(Boolean)
      .join(' ') ||
    (conflict.serverState['name'] as string) ||
    'Fiche'

  const ENTITES: Record<string, string> = {
    customer: 'Client',
    item: 'Article',
    supplier: 'Fournisseur',
  }

  async function apply(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      await resolveConflict(storeId, conflict!, choice)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Résolution impossible')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open fullWidth maxWidth="sm" onClose={busy ? undefined : onClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <WarningIcon color="error" />
        <Box sx={{ flex: 1 }}>Conflit de modification</Box>
        <IconButton onClick={onClose} disabled={busy} aria-label="Fermer" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {ENTITES[conflict.entity] ?? conflict.entity} « {nom} »
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
          Modifié en même temps sur deux appareils.
        </Typography>

        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              bgcolor: 'action.hover',
              px: 3,
              py: 2,
              gap: 3,
            }}
          >
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Votre version
            </Typography>
            <Typography variant="overline" sx={{ color: 'text.secondary' }}>
              Version du serveur
            </Typography>
          </Box>

          {fields.map((field) => (
            <Box
              key={field}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 3,
                px: 3,
                py: 3,
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {label(field)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: choice === 'local' ? 600 : 400 }}
                >
                  {display(conflict.localPayload[toCamel(field)])}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {label(field)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: choice === 'server' ? 600 : 400 }}
                >
                  {display(conflict.serverState[toCamel(field)])}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Alert severity="info" variant="outlined" sx={{ mt: 4 }}>
          {fields.length} champ{fields.length > 1 ? 's' : ''} en conflit. Les autres ont été
          fusionnés automatiquement.
        </Alert>

        <RadioGroup
          value={choice}
          onChange={(e) => setChoice(e.target.value as 'local' | 'server')}
          sx={{ mt: 3 }}
        >
          <FormControlLabel value="local" control={<Radio />} label="Garder ma version" />
          <FormControlLabel
            value="server"
            control={<Radio />}
            label="Garder la version du serveur"
          />
        </RadioGroup>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }} role="alert">
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 5, py: 4 }}>
        {/* « Reporter » ferme sans trancher : le conflit reste en attente. Un
            utilisateur au comptoir, client devant lui, doit pouvoir remettre
            l'arbitrage à plus tard sans rien perdre. */}
        <Button onClick={onClose} disabled={busy}>
          Reporter
        </Button>
        <Button variant="contained" onClick={() => void apply()} disabled={busy}>
          {busy ? 'Résolution…' : 'Résoudre'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
